import React, { useMemo, useState } from 'react';

import { Typography } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import Loader from 'components/Loader';
import assetRopaApi from 'config/api/assetRopa/assetRopa';
import {
  mergeOwnerWorkspaceListAssetWithDetail,
  resolveOverlayEmptyStateMessageKey,
} from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import type { OwnerWorkspaceAsset } from 'config/api/assetRopa/assetRopaWorkspace.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import type { AssetProcessingActivityReviewRow, AssetRopaReviewStatus } from 'config/api/ropaReviews/ropaReviews.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import AssetRopaWarningChips from 'pages/ProcessingActivities/_shared/AssetRopaWarningChips/AssetRopaWarningChips';

import { mergeCurrentProcessingActivities } from '../../ProcessingActivitiesAssetReviewsPage/mapOrganizationWorkspaceAssetToReview';
import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import OwnerWorkspaceProcessingTable from './OwnerWorkspaceProcessingTable';

type CompletenessMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_workspace.completeness_status;
type ReviewStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.review_status;

type Props = {
  asset: OwnerWorkspaceAsset;
  hasActiveReview: boolean;
  organizationId?: number;
  onAddProcessingActivity: (asset: OwnerWorkspaceAsset) => void;
  onOpenEditor: (row: AssetProcessingActivityReviewRow, asset: OwnerWorkspaceAsset, mode: 'view' | 'edit') => void;
  onOpenDpia?: (row: AssetProcessingActivityReviewRow) => void;
  onRemoveProcessingActivity: (row: AssetProcessingActivityReviewRow, asset: OwnerWorkspaceAsset) => void;
  onViewDpiaHistory?: (row: AssetProcessingActivityReviewRow) => void;
};

const formatDate = (value: string) => {
  try {
    return format(new Date(value), 'yyyy-MM-dd');
  } catch {
    return value;
  }
};

const getCompletenessBadgeClass = (status: OwnerWorkspaceAsset['informationalCompleteness'], styles: ReturnType<typeof useStyles>) => {
  if (status === 'complete') {
    return styles.statusBadgeSuccess;
  }

  if (status === 'no_processing_activities') {
    return styles.statusBadgeInfo;
  }

  return styles.statusBadgeWarn;
};

const getReviewStatusBadgeClass = (status: AssetRopaReviewStatus, styles: ReturnType<typeof useStyles>) => {
  switch (status) {
    case 'accepted':
      return styles.statusBadgeSuccess;
    case 'submitted':
      return styles.statusBadgeInfo;
    case 'clarification_requested':
      return styles.statusBadgeDanger;
    default:
      return styles.statusBadgeWarn;
  }
};

const getOverlayEmptyStateMessage = (messageKey: string | undefined, t: (message: string | string[]) => string): string => {
  const overlayMessages = processing_activities_messages.asset_ropa.owner_workspace.overlay_empty_state as Record<string, string[]>;
  const resolvedKey = messageKey ? resolveOverlayEmptyStateMessageKey(messageKey) : undefined;
  const message = resolvedKey ? overlayMessages[resolvedKey] : undefined;

  if (message) {
    return t(message);
  }

  return t(processing_activities_messages.asset_ropa.panel.no_processing_activities_body);
};

const OwnerWorkspaceAssetPanel: React.FC<Props> = ({
  asset,
  hasActiveReview,
  organizationId,
  onAddProcessingActivity,
  onOpenDpia,
  onOpenEditor,
  onRemoveProcessingActivity,
  onViewDpiaHistory,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const panelMessages = processing_activities_messages.asset_ropa.owner_workspace.panel;
  const completenessKey = asset.informationalCompleteness as CompletenessMessageKey;
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldLoadDetail = Boolean(
    organizationId && isExpanded && asset.processingActivitiesCount > 0 && asset.processingActivityReviews.length === 0,
  );

  const detailQuery = useQuery(
    [QUERY_KEYS.ASSET_ROPA_MINE_DETAIL, organizationId, asset.assetRopaId],
    async () => {
      const { data } = await assetRopaApi.getMineDetail(organizationId as number, asset.assetRopaId);
      return data;
    },
    {
      enabled: shouldLoadDetail,
      retry: false,
    },
  );

  const resolvedAsset = useMemo(() => {
    const currentAsset =
      detailQuery.data?.assetRopaId === asset.assetRopaId ? mergeOwnerWorkspaceListAssetWithDetail(asset, detailQuery.data) : asset;
    return {
      ...currentAsset,
      processingActivityReviews: mergeCurrentProcessingActivities(
        currentAsset.processingActivityReviews,
        currentAsset.reviewOverlay?.processingActivityReviews ?? [],
        hasActiveReview && currentAsset.includedInCurrentReview,
        hasActiveReview,
      ),
    };
  }, [asset, detailQuery.data, hasActiveReview]);

  const canEditAsset = resolvedAsset.canEdit !== false;

  const handleAddProcessingActivityClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onAddProcessingActivity(resolvedAsset);
  };

  const addProcessingActivityButton = canEditAsset ? (
    <ColoredButton
      className=''
      customColor='secondary'
      disabled={false}
      icon={false}
      onClick={handleAddProcessingActivityClick}
      showLoader={false}
      startIcon={<AddIcon />}
      textLike={false}
      variant='outlined'
    >
      {t(processing_activities_messages.asset_ropa.actions.add_processing_activity)}
    </ColoredButton>
  ) : null;

  const overlayEmptyState = resolvedAsset.reviewOverlay?.emptyState;
  const hasReviewOverlay = Boolean(resolvedAsset.reviewOverlay);
  const showNotIncludedInCurrentReview = hasActiveReview && resolvedAsset.includedInCurrentReview === false;
  const reviewStatus = resolvedAsset.reviewOverlay?.reviewStatus ?? resolvedAsset.reviewStatus;

  const renderPanelContent = () => {
    if (detailQuery.isLoading) {
      return <Loader inner />;
    }

    if (detailQuery.isError) {
      return (
        <div className={styles.emptyProcessingState}>
          <Typography color='textSecondary' variant='body2'>
            {t(processing_activities_messages.asset_ropa.errors.load_failed)}
          </Typography>
        </div>
      );
    }

    if (resolvedAsset.processingActivitiesCount === 0 || overlayEmptyState?.isEmpty) {
      const emptyBody =
        overlayEmptyState?.isEmpty && overlayEmptyState.messageKey
          ? getOverlayEmptyStateMessage(overlayEmptyState.messageKey, t)
          : t(processing_activities_messages.asset_ropa.panel.no_processing_activities_body);

      return (
        <div className={styles.emptyProcessingState}>
          <Typography variant='h3'>{t(processing_activities_messages.asset_ropa.panel.no_processing_activities_title)}</Typography>
          <Typography color='textSecondary'>{emptyBody}</Typography>
        </div>
      );
    }

    if (resolvedAsset.processingActivityReviews.length === 0) {
      return (
        <div className={styles.emptyProcessingState}>
          <Typography color='textSecondary' variant='body2'>
            {t(processing_activities_messages.asset_ropa.errors.load_failed)}
          </Typography>
        </div>
      );
    }

    return (
      <>
        <div className={styles.assetToolbar}>
          <div className={styles.assetToolbarNote}>{t(panelMessages.completeness_note)}</div>
        </div>
        <OwnerWorkspaceProcessingTable
          canEdit={canEditAsset}
          onOpenDpia={onOpenDpia}
          onOpenEditor={(row, mode) => onOpenEditor(row, resolvedAsset, mode)}
          onRemove={row => onRemoveProcessingActivity(row, resolvedAsset)}
          onViewDpiaHistory={onViewDpiaHistory}
          rows={resolvedAsset.processingActivityReviews}
          showReviewStatus={hasReviewOverlay}
        />
      </>
    );
  };

  return (
    <details className={styles.assetPanel} onToggle={event => setIsExpanded(event.currentTarget.open)}>
      <summary className={styles.assetSummary}>
        <div className={styles.assetSummaryMain}>
          <div className={styles.assetName}>{resolvedAsset.assetName}</div>
          <div className={styles.assetMeta}>
            <span>
              {resolvedAsset.assetTypeName} ·{' '}
              {t(panelMessages.updated, { date: formatDate(resolvedAsset.lastUpdatedAt), user: resolvedAsset.lastUpdatedByName ?? '—' })}
            </span>
          </div>
        </div>
        <div className={styles.workspaceSummaryGrid}>
          <div>
            <div className={styles.summaryLabel}>{t(panelMessages.owner)}</div>
            <div className={styles.summaryValue}>
              {resolvedAsset.ownerName ?? t(processing_activities_messages.asset_ropa.panel.not_assigned)}
            </div>
          </div>
          <div>
            <div className={styles.summaryLabel}>{t(panelMessages.operational_owner)}</div>
            <div className={styles.summaryValue}>
              {resolvedAsset.operationalOwnerName ?? t(processing_activities_messages.asset_ropa.panel.not_assigned)}
            </div>
          </div>
          <div>
            <div className={styles.summaryLabel}>{t(panelMessages.processing_activities)}</div>
            <div className={styles.summaryValue}>{resolvedAsset.processingActivitiesCount}</div>
          </div>
          <div className={styles.assetSummaryStatus}>
            <span className={clsx(styles.statusBadge, getCompletenessBadgeClass(resolvedAsset.informationalCompleteness, styles))}>
              {t(processing_activities_messages.asset_ropa.owner_workspace.completeness_status[completenessKey])}
            </span>
            {hasReviewOverlay && (
              <span className={clsx(styles.statusBadge, getReviewStatusBadgeClass(reviewStatus, styles))}>
                {t(processing_activities_messages.asset_ropa.review_status[reviewStatus as ReviewStatusMessageKey])}
              </span>
            )}
            {showNotIncludedInCurrentReview && (
              <span className={clsx(styles.statusBadge, styles.statusBadgeInfo)}>{t(panelMessages.not_included_in_current_review)}</span>
            )}
            <AssetRopaWarningChips inline warnings={resolvedAsset.warnings} />
            {resolvedAsset.gdprRelevanceStatus === 'currently_indicated' && (
              <span className={clsx(styles.statusBadge, styles.statusBadgeSuccess)}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance.currently_indicated)}
              </span>
            )}
            {resolvedAsset.gdprRelevanceStatus === 'no_longer_indicated' && (
              <span className={clsx(styles.statusBadge, styles.statusBadgeInfo)}>
                {t(processing_activities_messages.asset_ropa.owner_workspace.gdpr_relevance.no_longer_indicated)}
              </span>
            )}
          </div>
        </div>
        {addProcessingActivityButton && (
          <div
            className={styles.assetSummaryActions}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
            role='presentation'
          >
            {addProcessingActivityButton}
          </div>
        )}
      </summary>
      <div className={styles.assetPanelContent}>{renderPanelContent()}</div>
    </details>
  );
};

export default OwnerWorkspaceAssetPanel;
